import Cookies from 'universal-cookie';
import log from 'loglevel';
import { ADMIN_CLIENT_API_ULR } from '../util/constants';

// Helpers
import { extractBaseDomain } from "../util/domain.helper";

// const API_URL = window.location.hostname.includes('192.168.') ?
//   'http://192.168.56.102:60070/api/v1/admin' : '/api/v1/admin';

const API_URL = window.location.hostname.includes('127.0.') ?
  'http://127.0.0.1:60070/api/v1/admin' :
  ADMIN_CLIENT_API_ULR + '/api/v1/admin';

// Custom error class for MFA required
class MfaRequiredError extends Error {
  constructor(mfaData) {
    super('MFA_REQUIRED');
    this.name = 'MfaRequiredError';
    this.mfa_token = mfaData.mfa_token;
    this.user_id = mfaData.user_id;
  }
}

// Custom error class for account locked
class AccountLockedError extends Error {
    constructor() {
        super('Your account has been locked. Please contact customer support.');
        this.name = 'AccountLockedError';
    }
}

// Custom error class for login timeout
class LoginFailNthError extends Error {
    constructor(data) {
        super('You login failed too many times.');
        this.name = 'LoginFailNthError';
        this.retry_after = data?.retry_after || 60;
    }
}

const login = async (username, password) => {
  const uri = `/users/authentication`;

  // Use POST method with body instead of GET with query params
  const requestBody = {
    user_name: username,
    password: password
  };

  const request = new Request(API_URL + uri, {
    method: 'POST',
    headers: new Headers({'Content-Type': 'application/json'}),
    body: JSON.stringify(requestBody)
  });
  const response = await fetch(request);
  log.debug(`{authProvider} login response:`, response);

  if (response.status < 200 || response.status >= 300) {  // statusText: "Unauthorized"
    // Try to get error details from response
    const errorJson = await response.json();
    log.error(`{authProvider} login error response:`, errorJson);
    if(errorJson.name === 'account_locked') {
      throw new AccountLockedError();
    } else if (errorJson.name === 'fail_nth') {
      throw new LoginFailNthError(errorJson.data);
    }
    const errorMessage = errorJson.message || response.statusText || 'Authentication failed';
    throw new Error(errorMessage);
  }

  const responseJson = await response.json();
  log.debug(`{authProvider} login responseJson:`, responseJson);

  const { data, status } = responseJson;
  log.debug(`{authProvider} data:`, data);
  log.debug(`{authProvider} status:`, status);

  // Check if response is valid
  if (!data) {
    log.error(`{authProvider} data is undefined in response. Full response:`, responseJson);
    throw new Error('Invalid response from server. Please check if the backend services are running correctly.');
  }

  // Check if MFA setup is required (POST endpoint returns status: "mfa_setup_required")
  if (status === 'mfa_setup_required') {
    // Store the MFA token in cookies so httpClient can use it automatically
    // This token will be used to call the MFA setup endpoint
    // Note: In production, this should be httpOnly and secure cookie set by the backend
    const cookies = new Cookies();
    const domain = extractBaseDomain(window.location.hostname);
    log.info("{mfa_setup_required} window.location.hostname:", window.location.hostname);
    log.info("{mfa_setup_required} domain:", domain);
    // Set cookie with 10-minute expiration to prevent token from persisting indefinitely
    // If user closes tab or navigates away, the token will expire automatically
    const expirationDate = new Date();
    expirationDate.setMinutes(expirationDate.getMinutes() + 10);

    cookies.set('token', data.mfa_token, {
      path: '/',
      expires: expirationDate,
      domain
    });

    // Redirect to MFA setup page with a flag in the URL
    // This avoids storing sensitive data in localStorage (XSS vulnerability)
    // The MfaSetup component will decode the user ID from the token in the cookie
    window.location.href = '/#/mfa-setup?setup=required';
    // Return a promise that never resolves to prevent further navigation
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return new Promise(() => { });
  }

  // Check if MFA verification is required (POST endpoint returns status: "mfa_required")
  if (status === 'mfa_required') {
    // Throw MFA error so Login component can handle it
    // The Login component checks for error.name === "MfaRequiredError"
    throw new MfaRequiredError(data);
  }

  let { token } = data;

  // User has MFA enabled - store token in cookie
  //localStorage.setItem('token', token);
  updateJurisdictionsCookie(data, token);

  // Testing
  getUserFromCookie();

  // Return resolved promise to signal successful login
  return Promise.resolve();
}

const loginMfa = async (user_name, otp, mfa_token) => {
  const uri = `/users/mfa/authenticate`;

  const response = await fetch(API_URL + uri, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${mfa_token}`
    },
    body: JSON.stringify({
      token: otp
    })
  });

  log.debug(`{loginMfa} response:`, response);

  if (response.status < 200 || response.status >= 300) {
    const errorJson = await response.json();
    log.error(`{loginMfa} error response:`, errorJson);
    if(errorJson.name === 'account_locked') {
      throw new AccountLockedError();
    } else if (errorJson.name === 'fail_nth') {
      throw new LoginFailNthError(errorJson.data);
    }
    const errorMessage = errorJson.message || response.statusText || 'Authentication failed';
    throw new Error(errorMessage);
  }

  const responseJson = await response.json();
  log.debug(`{loginMfa} responseJson:`, responseJson);

  const { data, status, message } = responseJson;

  if (status !== "success") {
    // Use the specific error message from the backend if available
    throw new Error(message || "MFA authentication failed");
  }

  // Extract user and token from response
  let { token, user } = data;

  // Store token and user data in cookies
  updateJurisdictionsCookie(user, token);
  getUserFromCookie();

  // Return resolved promise to signal successful login
  return Promise.resolve();
}

/**
 * This implements getPermissions, a defined function on the react-admin authProvider interface.
 * It returns an array of roles, e.g. ['un_admin, 'er_admin]
 * See https://marmelab.com/react-admin/AuthProviderWriting.html#getpermissions
 * and https://marmelab.com/react-admin/AuthRBAC.html#authprovider-methods
 * @returns {Promise<string>}
 */
const getAuthPermissions = async () => {
  getUserFromCookie();
  //const role = localStorage.getItem('role');
  const roles = _getRoles(getFromCookie('jurisdictions'));
  const permissions = roles.join(',');
  log.debug('{getAuthPermissions} permissions:', permissions);
  //throw('{getAuthPermissions} debug');
  //return permissions ? Promise.resolve(permissions) : Promise.reject();
  return permissions;
}

const logout = () => {
  //TODO: delete cookie
  //localStorage.removeItem('token');
  removeCookieItems();
  // const user = getUserFromCookie();
  // log.debug("{logout} Logged out. User should have empty fields:", user);
  //console.trace();
  log.debug("{logout} Logged out");
  return Promise.resolve();
}


// Create JWT - exported for use in MFA setup
export const updateJurisdictionsCookie = function(data, token) {
  const cookies = new Cookies();
  log.debug("{updateJurisdictionsCookie} window.location.hostname:", window.location.hostname);
  const domain = extractBaseDomain(window.location.hostname);
  log.debug("{updateJurisdictionsCookie} domain:", domain);
  cookies.set('token', token, {path: '/', domain});
  cookies.set('jurisdictions', data.jurisdictions, {path: '/', domain});
  cookies.set('first_name', data.first_name, {path: '/', domain});
  cookies.set('last_name', data.last_name, {path: '/', domain});
  cookies.set('rep_uname', data.rep_uname, {path: '/', domain});
  // Note: We don't store the entire user object in cookies
  // All needed fields are stored individually above
  // User data can be decoded from the JWT token if needed
};

export const getUserFromCookie = () => {
  let user = {};
  const cookies = new Cookies();
  user.token = cookies.get('token');
  user.first_name = cookies.get('first_name');
  user.last_name = cookies.get('last_name');
  user.jurisdictions = cookies.get('jurisdictions');
  user.rep_uname = cookies.get('rep_uname');
  return user;
};

/**
 * Complete MFA setup - called from MfaSetup component after successful verification
 * This function sets cookies and localStorage, then returns a resolved promise
 * so that React Admin can handle navigation smoothly (same as login flow)
 * @param {object} userData - User data from MFA verify response
 * @param {string} token - JWT token from MFA verify response
 * @returns {Promise} - Resolved promise to signal successful setup
 */
export const completeMfaSetup = (userData, token) => {
  // Store token and user data in cookies (same as loginMfa)
  updateJurisdictionsCookie(userData, token);

  // Log user data for debugging
  getUserFromCookie();

  // Return resolved promise to signal successful setup
  // This allows React Admin to handle navigation smoothly
  return Promise.resolve();
};

const getFromCookie = name => new Cookies().get(name);

/**
 * Gets the time in utc seconds given a date. From node-forge.http.
 *
 * @param d the date to use.
 *
 * @return the time in utc seconds.
 */
// const _getUtcTime = d => {
//   // const utc = +d + d.getTimezoneOffset() * 60000;
//   //+d + d.getTimezoneOffset() * 60000;
//   const tzo = +d + d.getTimezoneOffset() * 60000;
//   const t1 = Math.floor(+new Date() / 1000);
//   const t2 = Math.floor((new Date()).getTime() / 1000);
//   console.log(`{_getUtcTime} t1: ${t1}, t2: ${t2}, tzo: ${tzo}`);
//   return Math.floor(+new Date() / 1000);
// };

/**
 * Checks to see if the given cookie has expired. If the cookie's max-age
 * plus its created time is less than the time now, it has expired, unless
 * its max-age is set to -1 which indicates it will never expire.
 *
 * @param cookie the cookie to check.
 *
 * @return true if it has expired, false if not.
 */
// const _hasJwtExpired = (token) => {
//   if (!token) return false;
//   const jwt = jsonwebtoken.decode(token);
//   log.debug("{_hasCookieExpired} jwt:", jwt);
//   if (jwt) {
//     const expiry = jwt.exp;
//     log.debug("{_hasCookieExpired} expiry:", expiry);
//     const now = new Date();
//     log.debug("{_hasCookieExpired} expired?:", now.getTime() > expiry * 1000);
//     return now.getTime() > expiry * 1000;
//   }
//   return false;
// }

// const _xhasCookieExpired = () => {
//   const maxAge = getFromCookie('maxAge');
//   const created = getFromCookie('created');
//   let rval = false;
//   log.debug("{_hasCookieExpired} maxAge:", maxAge);
//   if (maxAge !== -1) {
//     const now = _getUtcTime(new Date());
//     const expires = created + maxAge;
//     if (expires <= now) {
//       rval = true;
//     }
//   }
//   return rval;
// };

const removeCookieItems = () => {
  const cookies = new Cookies();
  log.debug("{removeCookieItems} window.location.hostname:", window.location.hostname);
  const domain = extractBaseDomain(window.location.hostname);
  log.debug("{removeCookieItems} domain:", domain);
  cookies.remove('token', {path: '/', domain });
  cookies.remove('first_name', {path: '/', domain});
  cookies.remove('last_name', {path: '/', domain});
  cookies.remove('jurisdictions', {path: '/', domain});
  cookies.remove('rep_uname', {path: '/', domain});
};

/**
 * Convert jurisdictions object to roles.
 * E.g. {verified: ["5f43d7796d420c2bf8e5275a"], superadmin: ["5f43d7796d420c2bf8e5275a"]}
 * to: ['verified', 'superadmin']
 * @param jurisdictionsObj
 * @returns {[]}
 */

const _getRoles = jurisdictionsObj => jurisdictionsObj ? Object.keys(jurisdictionsObj).map(role => role) : [];

/**
 * Each time the dataProvider returns an error, react-admin calls authProvider.checkError()
 */
const authProvider = {
  login: ({username, password, otp, mfa_token, user_id }) => {
    // If OTP is provided, this is an MFA login
    if (otp && mfa_token) {
      return loginMfa(username, otp, mfa_token, user_id);
    }
    return login(username, password);
  },
  logout: () => logout(),
  checkError: (error) => {
    const status = error.status;
    log.debug(`{authProvider.checkError} status:`, status);
    if (status === 401 || status === 403) {
      // localStorage.removeItem('auth');
      log.debug(`{authProvider.checkError} checkError reject`);
      return Promise.reject();
    }
    // other error code (404, 500, etc): no need to log out
    return Promise.resolve();
  },
  // checkError: (error) => {
  //   log.debug(`{checkError} error:`, error);
  //   const status = error.status;
  //   log.debug(`{checkError} status:`, status);
  //   return _hasJwtExpired(getFromCookie('token')) ? Promise.reject() : Promise.resolve();
  // },
  checkAuth: () => {
    const token = getFromCookie('token');
    if (!token) {
      return Promise.reject();
    }

    // Decode the token to check if it's a temporary MFA token
    // Temporary MFA tokens should NOT grant access to the main application
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        return Promise.reject(); // Invalid token format
      }

      const payload = JSON.parse(atob(tokenParts[1]));

      // Check if this is a temporary MFA token
      if (payload.type === 'mfa_temp') {
        log.debug('{checkAuth} Temporary MFA token detected - rejecting access');
        return Promise.reject(); // Temporary MFA tokens cannot access the main app
      }

      // Check if token is expired
      if (payload.exp && Date.now() >= payload.exp * 1000) {
        log.debug('{checkAuth} Token expired - rejecting access');
        return Promise.reject();
      }

      // Valid full token
      return Promise.resolve();
    } catch (error) {
      log.error('{checkAuth} Error decoding token:', error);
      return Promise.reject();
    }
  },
  getPermissions: () => getAuthPermissions(),

  // Enable this to get profile picture of user in header
  getIdentity: () => {
    const {first_name, last_name} = getUserFromCookie();
    return Promise.resolve({
      id: 'user',
      fullName: `${first_name} ${last_name}`,
      // avatar:
      //   'data:image/jpeg;base64,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',
    })
  }
};

export default authProvider;
